import { parseAttractionExperience } from '@routellers/types/dist/city-attraction/attraction-experience';
import {
    Button,
    Group,
    NumberInput,
    MultiSelect,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
    AttractionContentCard,
    AttractionFunFact,
} from '@routellers/types/dist/city-attraction/attraction-content';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { AttractionAudioPotentialEnum } from '@routellers/types/dist/city-attraction/enum/attraction-audio-potential.enum';
import { AttractionCategoryEnum } from '@routellers/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionThemeEnum } from '@routellers/types/dist/city-attraction/enum/attraction-theme.enum';
import { AttractionFameEnum } from '@routellers/types/dist/city-attraction/enum/attraction-fame.enum';
import { AttractionFunFactKindEnum } from '@routellers/types/dist/city-attraction/enum/attraction-fun-fact-kind.enum';
import { AttractionIndoorOutdoorEnum } from '@routellers/types/dist/city-attraction/enum/attraction-indoor-outdoor.enum';
import { AttractionLocaleStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-locale-status.enum';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { AttractionTierEnum } from '@routellers/types/dist/city-attraction/attraction-layers';
import { AttractionRouteRoleEnum } from '@routellers/types/dist/city-attraction/enum/attraction-route-role.enum';
import { AttractionSourceEnum } from '@routellers/types/dist/city-attraction/enum/attraction-source.enum';
import { UpsertCityAttractionArtifactDto } from '@routellers/types/dist/city-attraction/upsert-city-attraction-artifact.dto';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { AttractionReadinessBadge } from '../components/attraction-readiness-badge';
import { DistrictSelect } from '../components/district-select';
import {
    emptyHours,
    hoursFrom,
    hoursPayload,
    unpairedHourDay,
    WeekHoursFields,
} from '../components/week-hours-fields';
import {
    EditorFooter,
    EntityEditorShell,
    LanguageChips,
    MediaDropzone,
    PhotoUpload,
    Section,
} from '../components/editor';
import { enumLabel, enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';
import { artifactsFromRo } from './attraction/artifacts-helpers';
import { StaffNotesPanel } from './staff-notes-panel';
import { ArtifactsPanel } from './attraction/artifacts-panel';
import { FactsPanel } from './attraction/facts-panel';
import { StoryBriefPanel } from './attraction/story-brief-panel';
import {
    emptyLocaleDraft,
    findLocale,
    hasLocaleContent,
    LocaleDraft,
    localeToDraft,
} from './attraction/locale-helpers';
import { CitySearchSelect } from './city-search-select';

type Props = {
    opened: boolean;
    zIndex?: number;
    onClose: () => void;
    attractionId: number | null;
    onSaved: () => void;
    /** After create, parent can switch to the new id. */
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
    initialTab?: string;
    initialCityId?: number | null;
};

const reviewColor: Record<AttractionReviewStatusEnum, string> = {
    [AttractionReviewStatusEnum.APPROVED]: 'green',
    [AttractionReviewStatusEnum.NEED_REVIEW]: 'orange',
    [AttractionReviewStatusEnum.REJECTED]: 'red',
    [AttractionReviewStatusEnum.REJECTED_MCP]: 'red',
};

type MetaForm = {
    title: string;
    address: string;
    category: AttractionCategoryEnum | null;
    fame: AttractionFameEnum | null;
    country: CountriesEnum | null;
    lat: number | null;
    lng: number | null;
    knownFor: string;
    description: string;
    cityId: number | null;
    districtId: number | null;
    hours: ReturnType<typeof emptyHours>;
    isFree: boolean;
    ticketRequired: boolean;
    bookingRequired: boolean;
    familyFriendly: boolean;
    rainFriendly: boolean;
    nightFriendly: boolean;
    visitDurationMin: number | null;
    visitDurationMax: number | null;
    rejectionReason: string;
    tags: string[];
    audioPotential: AttractionAudioPotentialEnum | null;
    mustSeeScore: number | null;
    wowScore: number | null;
    touristCrowdScore: number | null;
    localExperienceScore: number | null;
    routeRole: AttractionRouteRoleEnum | null;
    indoorOutdoor: AttractionIndoorOutdoorEnum | null;
    wikidataId: string;
    isFreePreview: boolean;
    tier: AttractionTierEnum | null;
    lightApproved: boolean;
};

type ActionKind = 'tts' | 'regenerate' | 'deepen' | 'cover' | 'archive';

/** NumberInput can hold '' when cleared — the API expects number | null. */
const numberOrNull = (value: number | string | null) => (typeof value === 'number' ? value : null);

const VISIT_FLAGS = [
    'isFree',
    'ticketRequired',
    'bookingRequired',
    'familyFriendly',
    'rainFriendly',
    'nightFriendly',
] as const;

export const AttractionEditor = ({
    opened,
    zIndex,
    onClose,
    attractionId,
    onSaved,
    onCreated,
    onDeleted,
    initialTab = 'details',
    initialCityId,
}: Props) => {
    const { t } = useTranslation();
    const isCreate = attractionId == null;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [moderating, setModerating] = useState(false);
    const [action, setAction] = useState<ActionKind | null>(null);
    const [entity, setEntity] = useState<CityAttractionRO | null>(null);
    const [tab, setTab] = useState(initialTab);
    const [tabDirty, setTabDirty] = useState(false);
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [localeDraft, setLocaleDraft] = useState<LocaleDraft>(emptyLocaleDraft());
    const [localeBaseline, setLocaleBaseline] = useState('');
    const [photos, setPhotos] = useState<string[]>([]);
    const [photosBaseline, setPhotosBaseline] = useState('');
    const [artifacts, setArtifacts] = useState<UpsertCityAttractionArtifactDto[]>([]);
    const [artifactsBaseline, setArtifactsBaseline] = useState('[]');

    const form = useForm<MetaForm>({
        initialValues: {
            title: '',
            address: '',
            category: AttractionCategoryEnum.ARCHITECTURE_LANDMARK,
            fame: AttractionFameEnum.WELL_KNOWN,
            country: null,
            lat: null,
            lng: null,
            knownFor: '',
            description: '',
            cityId: initialCityId ?? null,
            districtId: null,
            hours: emptyHours(),
            isFree: false,
            ticketRequired: false,
            bookingRequired: false,
            familyFriendly: false,
            rainFriendly: false,
            nightFriendly: false,
            visitDurationMin: null,
            visitDurationMax: null,
            rejectionReason: '',
            tags: [],
            audioPotential: null,
            mustSeeScore: null,
            wowScore: null,
            touristCrowdScore: null,
            localExperienceScore: null,
            routeRole: null,
            indoorOutdoor: null,
            wikidataId: '',
            isFreePreview: false,
            tier: null,
            lightApproved: false,
        },
    });

    const categoryOptions = useMemo(
        () => enumOptions(Object.values(AttractionCategoryEnum), t, 'enums.attractionCategory'),
        [t],
    );
    const fameOptions = useMemo(
        () => enumOptions(Object.values(AttractionFameEnum), t, 'enums.attractionFame'),
        [t],
    );
    const countryOptions = useMemo(
        () => enumOptions(Object.values(CountriesEnum), t, 'enums.countries'),
        [t],
    );
    const localeStatusOptions = useMemo(
        () => enumOptions(Object.values(AttractionLocaleStatusEnum), t, 'enums.localeStatus'),
        [t],
    );
    const tierOptions = useMemo(
        () => enumOptions(Object.values(AttractionTierEnum), t, 'enums.attractionTier'),
        [t],
    );
    const routeRoleOptions = useMemo(
        () => enumOptions(Object.values(AttractionRouteRoleEnum), t, 'enums.attractionRouteRole'),
        [t],
    );
    const indoorOutdoorOptions = useMemo(
        () =>
            enumOptions(
                Object.values(AttractionIndoorOutdoorEnum),
                t,
                'enums.attractionIndoorOutdoor',
            ),
        [t],
    );
    const funFactKindOptions = useMemo(
        () => enumOptions(Object.values(AttractionFunFactKindEnum), t, 'enums.funFactKind'),
        [t],
    );

    const applyEntity = useCallback(
        (data: CityAttractionRO) => {
            setEntity(data);
            form.setValues({
                title: data.title || '',
                address: data.address || '',
                category: data.category || AttractionCategoryEnum.ARCHITECTURE_LANDMARK,
                fame: data.fame || AttractionFameEnum.WELL_KNOWN,
                country: data.country || null,
                lat: data.lat ?? null,
                lng: data.lng ?? null,
                knownFor: data.knownFor || '',
                description: data.description || '',
                cityId: data.cityId ?? null,
                districtId: data.districtId ?? null,
                hours: hoursFrom(data.hours),
                isFree: data.routeMeta?.isFree === true,
                ticketRequired: data.routeMeta?.ticketRequired === true,
                bookingRequired: data.routeMeta?.bookingRequired === true,
                familyFriendly: data.routeMeta?.familyFriendly === true,
                rainFriendly: data.routeMeta?.rainFriendly === true,
                nightFriendly: data.routeMeta?.nightFriendly === true,
                visitDurationMin: data.routeMeta?.visitDurationMin ?? null,
                visitDurationMax: data.routeMeta?.visitDurationMax ?? null,
                rejectionReason: data.rejectionReason || '',
                tags: data.tags || [],
                audioPotential: data.audioPotential ?? null,
                mustSeeScore: data.mustSeeScore ?? null,
                wowScore: data.wowScore ?? null,
                touristCrowdScore: data.touristCrowdScore ?? null,
                localExperienceScore: data.localExperienceScore ?? null,
                routeRole: data.routeRole || null,
                indoorOutdoor: data.indoorOutdoor || null,
                wikidataId: data.wikidataId || '',
                isFreePreview: data.isFreePreview ?? false,
                tier: data.tier ?? null,
                lightApproved: !!data.lightApprovedAt,
            });
            form.resetDirty();
            setPhotos(data.photos || []);
            setPhotosBaseline(JSON.stringify(data.photos || []));
            const nextArtifacts = artifactsFromRo(data.artifacts);

            setArtifacts(nextArtifacts);
            setArtifactsBaseline(JSON.stringify(nextArtifacts));
        },
        [form],
    );

    const syncReadiness = useCallback(async () => {
        if (attractionId == null) return;

        try {
            const { data } = await api.cityAttractionsApi.getOne(attractionId);

            if (!data?.readiness) return;
            setEntity((prev) => (prev ? { ...prev, readiness: data.readiness } : prev));
            onSaved?.();
        } catch {
            /* the badge refreshes with the next save */
        }
    }, [attractionId, onSaved]);

    useEffect(() => {
        if (!opened) return;

        setTab(initialTab);
        setTabDirty(false);
        setLang(CONTENT_BASE_LANGUAGE);

        if (isCreate) {
            setEntity(null);
            form.reset();
            form.setFieldValue('cityId', initialCityId ?? null);
            setPhotos([]);
            setPhotosBaseline('[]');
            setArtifacts([]);
            setArtifactsBaseline('[]');
            setLocaleDraft(emptyLocaleDraft());
            setLocaleBaseline(JSON.stringify(emptyLocaleDraft()));
            setLoading(false);

            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityAttractionsApi.getOne(attractionId!);

                if (!cancelled && data) applyEntity(data);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                onClose();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, attractionId, initialTab, initialCityId]);

    useEffect(() => {
        if (!entity) return;

        const next = localeToDraft(findLocale(entity.locales, lang), entity.title);

        setLocaleDraft(next);
        setLocaleBaseline(JSON.stringify(next));
    }, [entity, lang]);

    const localeDirty = useMemo(
        () => JSON.stringify(localeDraft) !== localeBaseline,
        [localeDraft, localeBaseline],
    );
    const photosDirty = useMemo(
        () => JSON.stringify(photos) !== photosBaseline,
        [photos, photosBaseline],
    );
    const artifactsDirty = useMemo(
        () => JSON.stringify(artifacts) !== artifactsBaseline,
        [artifacts, artifactsBaseline],
    );

    useEffect(() => {
        if (tab === 'details') setTabDirty(form.isDirty());
        else if (tab === 'locales') setTabDirty(localeDirty);
        else if (tab === 'media') setTabDirty(photosDirty);
        else if (tab === 'artifacts') setTabDirty(artifactsDirty);
        else setTabDirty(false);
    }, [tab, form, localeDirty, photosDirty, artifactsDirty]);

    const handleTabChange = (next: string) => {
        if (tabDirty) {
            const ok = window.confirm(t('common.confirmSwitchTab'));

            if (!ok) return;
        }

        setTabDirty(false);
        setTab(next);
    };

    const saveDetails = async () => {
        if (!form.values.title.trim() || !form.values.category || !form.values.fame) {
            notifications.show({
                color: 'red',
                message: t('editors.attraction.titleCategoryFameRequired'),
            });

            return;
        }

        if (form.values.cityId == null) {
            notifications.show({ color: 'red', message: t('editors.attraction.cityRequired') });

            return;
        }

        const brokenDay = unpairedHourDay(form.values.hours);

        if (brokenDay) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.hoursPair', {
                    day: t(`enums.weekday.${brokenDay}`),
                }),
            });

            return;
        }

        const stored = entity?.routeMeta || {};
        // An untouched checkbox keeps «unknown» instead of turning it into «no».
        const flag = (key: (typeof VISIT_FLAGS)[number]) =>
            form.values[key] === (stored[key] === true) ? (stored[key] ?? null) : form.values[key];
        const visitMeta = {
            ...stored,
            isFree: flag('isFree'),
            ticketRequired: flag('ticketRequired'),
            bookingRequired: flag('bookingRequired'),
            familyFriendly: flag('familyFriendly'),
            rainFriendly: flag('rainFriendly'),
            nightFriendly: flag('nightFriendly'),
            visitDurationMin: numberOrNull(form.values.visitDurationMin),
            visitDurationMax: numberOrNull(form.values.visitDurationMax),
        };

        setSaving(true);

        try {
            if (isCreate) {
                if (!form.values.country) {
                    notifications.show({
                        color: 'red',
                        message: t('editors.attraction.countryRequired'),
                    });

                    return;
                }

                const { data } = await api.cityAttractionsApi.create({
                    data: {
                        title: form.values.title.trim(),
                        address: form.values.address.trim() || null,
                        category: form.values.category,
                        fame: form.values.fame,
                        country: form.values.country,
                        cityId: form.values.cityId,
                        districtId: form.values.districtId,
                        hours: hoursPayload(form.values.hours),
                        routeMeta: visitMeta,
                        lat: form.values.lat,
                        lng: form.values.lng,
                        knownFor: form.values.knownFor.trim() || null,
                        description: form.values.description.trim() || null,
                        source: AttractionSourceEnum.MANUAL,
                    },
                });

                if (data) {
                    notifications.show({
                        color: 'green',
                        message: t('editors.attraction.created'),
                    });
                    onSaved();
                    onCreated?.(data.id);
                }
            } else {
                const { data } = await api.cityAttractionsApi.update({
                    id: attractionId!,
                    data: {
                        title: form.values.title.trim(),
                        address: form.values.address.trim() || null,
                        category: form.values.category,
                        fame: form.values.fame,
                        lat: form.values.lat,
                        lng: form.values.lng,
                        cityId: form.values.cityId,
                        districtId: form.values.districtId,
                        hours: hoursPayload(form.values.hours),
                        routeMeta: visitMeta,
                        tags: form.values.tags,
                        audioPotential: form.values.audioPotential,
                        mustSeeScore: numberOrNull(form.values.mustSeeScore),
                        wowScore: numberOrNull(form.values.wowScore),
                        touristCrowdScore: numberOrNull(form.values.touristCrowdScore),
                        localExperienceScore: numberOrNull(form.values.localExperienceScore),
                        routeRole: form.values.routeRole,
                        indoorOutdoor: form.values.indoorOutdoor,
                        wikidataId: form.values.wikidataId.trim() || null,
                        isFreePreview: form.values.isFreePreview,
                        tier: form.values.tier,
                        lightApproved: form.values.lightApproved,
                    },
                });

                if (data) applyEntity(data);
                notifications.show({ color: 'green', message: t('editors.attraction.saved') });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveLocale = async (status?: AttractionLocaleStatusEnum) => {
        if (attractionId == null) return;

        setSaving(true);

        try {
            const { data } = await api.cityAttractionsApi.upsertLocale({
                id: attractionId,
                data: {
                    language: lang,
                    experience: localeDraft.experienceJson.trim() ? parseAttractionExperience(JSON.parse(localeDraft.experienceJson)) : null,
                    title: localeDraft.title.trim(),
                    description: localeDraft.description.trim() || null,
                    knownFor: localeDraft.knownFor.trim() || null,
                    hoursNote: localeDraft.hoursNote.trim() || null,
                    priceNote: localeDraft.priceNote.trim() || null,
                    visitNote: localeDraft.visitNote.trim() || null,
                    content: localeDraft.content,
                    guideAudioUrl: localeDraft.guideAudioUrl,
                    guideAudioDurationSec: localeDraft.guideAudioDurationSec,
                    status: status || localeDraft.status,
                },
            });

            if (data) applyEntity(data);
            const next = { ...localeDraft, status: status || localeDraft.status };

            setLocaleDraft(next);
            setLocaleBaseline(JSON.stringify(next));
            notifications.show({
                color: 'green',
                message: t('editors.attraction.localeSaved'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const savePhotos = async () => {
        if (attractionId == null) return;

        setSaving(true);

        try {
            const { data } = await api.cityAttractionsApi.update({
                id: attractionId,
                data: { photos },
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message: t('editors.attraction.photosSaved'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveArtifacts = async () => {
        if (attractionId == null) return;

        const invalid = artifacts.find((a) => !a.title?.trim());

        if (invalid) {
            notifications.show({
                color: 'red',
                message: t('editors.attraction.artifactTitleRequired'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.cityAttractionsApi.replaceArtifacts({
                id: attractionId,
                data: {
                    artifacts: artifacts.map((a, index) => ({
                        ...a,
                        title: a.title.trim(),
                        sortOrder: index,
                    })),
                },
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message: t('editors.attraction.artifactsSaved'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (attractionId == null) return;

        const ok = window.confirm(t('editors.attraction.deleteConfirm', { id: attractionId }));

        if (!ok) return;

        setDeleting(true);

        try {
            await api.cityAttractionsApi.remove(attractionId);
            notifications.show({ color: 'green', message: t('editors.attraction.deleted') });
            onDeleted?.();
            onSaved();
            onClose();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setDeleting(false);
        }
    };

    const moderate = async (reviewStatus: AttractionReviewStatusEnum) => {
        if (attractionId == null) return;

        setModerating(true);

        try {
            const { data } = await api.cityAttractionsApi.moderate({
                id: attractionId,
                data: {
                    reviewStatus,
                    rejectionReason:
                        reviewStatus === AttractionReviewStatusEnum.REJECTED ||
                        reviewStatus === AttractionReviewStatusEnum.REJECTED_MCP
                            ? form.values.rejectionReason.trim() || null
                            : null,
                },
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message:
                    reviewStatus === AttractionReviewStatusEnum.APPROVED
                        ? t('editors.attraction.approved')
                        : t('editors.attraction.rejected'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setModerating(false);
        }
    };

    const patchCard = (index: number, values: Partial<AttractionContentCard>) =>
        setLocaleDraft((d) => ({
            ...d,
            content: {
                ...d.content,
                cards: (d.content.cards || []).map((c, i) =>
                    i === index ? { ...c, ...values } : c,
                ),
            },
        }));

    const patchFunFact = (values: Partial<AttractionFunFact>) =>
        setLocaleDraft((d) => ({
            ...d,
            content: {
                ...d.content,
                funFact: {
                    body: '',
                    kind: AttractionFunFactKindEnum.FACT,
                    ...d.content.funFact,
                    ...values,
                },
            },
        }));

    const generateTts = async () => {
        if (attractionId == null) return;

        setAction('tts');

        try {
            const { data } = await api.cityAttractionsApi.generateAudio({
                id: attractionId,
                data: {
                    language: lang,
                    text: localeDraft.content.guideTalk?.trim() || undefined,
                },
            });

            if (data) {
                // The server persists url + duration; re-read them so an unsaved
                // draft does not overwrite the fresh duration on the next save.
                const { data: fresh } = await api.cityAttractionsApi.getOne(attractionId);
                const freshLocale = findLocale(fresh?.locales, lang);
                const audio = {
                    guideAudioUrl: freshLocale?.guideAudioUrl ?? data.url,
                    guideAudioDurationSec: freshLocale?.guideAudioDurationSec ?? null,
                };

                setLocaleDraft((d) => ({ ...d, ...audio }));
                setLocaleBaseline((b) =>
                    JSON.stringify({ ...(JSON.parse(b) as LocaleDraft), ...audio }),
                );
                if (fresh?.readiness) {
                    setEntity((prev) => (prev ? { ...prev, readiness: fresh.readiness } : prev));
                }
                notifications.show({
                    color: 'green',
                    message: t('editors.attraction.actions.ttsDone'),
                });
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAction(null);
        }
    };

    const regenerateContent = async () => {
        if (attractionId == null) return;

        const ok = window.confirm(
            t('editors.attraction.actions.regenerateConfirm', { lang: lang.toUpperCase() }),
        );

        if (!ok) return;

        setAction('regenerate');

        try {
            const { data } = await api.cityAttractionsApi.generateContent({
                id: attractionId,
                data: { language: lang },
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message: t('editors.attraction.actions.regenerated'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAction(null);
        }
    };

    const deepenResearch = async () => {
        if (attractionId == null) return;

        setAction('deepen');

        try {
            const { data } = await api.cityAttractionsApi.deepen({
                id: attractionId,
                data: { cityId: entity?.cityId ?? form.values.cityId },
            });

            if (data?.entity) applyEntity(data.entity);
            notifications.show({
                color: 'green',
                message: t('editors.attraction.actions.deepenDone', {
                    count: data?.filled?.length ?? 0,
                }),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAction(null);
        }
    };

    const generateCoverImage = async () => {
        if (attractionId == null) return;

        setAction('cover');

        try {
            const { data } = await api.cityAttractionsApi.generateCover({
                id: attractionId,
                data: {},
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message: t('editors.attraction.actions.coverDone'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAction(null);
        }
    };

    const toggleArchive = async () => {
        if (attractionId == null) return;

        const archived = !entity?.archived;

        if (archived) {
            const ok = window.confirm(
                t('editors.attraction.actions.archiveConfirm', { id: attractionId }),
            );

            if (!ok) return;
        }

        setAction('archive');

        try {
            const { data } = await api.cityAttractionsApi.archive({
                id: attractionId,
                data: { archived },
            });

            notifications.show({
                color: 'green',
                message: archived
                    ? t('editors.attraction.actions.archived')
                    : t('editors.attraction.actions.unarchived'),
            });
            onSaved();

            if (archived) {
                onClose();
            } else if (data) {
                applyEntity(data);
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setAction(null);
        }
    };

    const contentLanguages = useMemo(
        () => contentLanguagesForCountry(form.values.country || entity?.country),
        [form.values.country, entity?.country],
    );

    useEffect(() => {
        if (!contentLanguages.includes(lang)) {
            setLang(contentLanguages[0] || CONTENT_BASE_LANGUAGE);
        }
    }, [contentLanguages, lang]);

    const chips = contentLanguages.map((l) => {
        const locale = findLocale(entity?.locales, l);
        let mark: 'empty' | 'filled' | 'reviewed' | 'dirty' = 'empty';

        if (l === lang && localeDirty) mark = 'dirty';
        else if (locale?.status === AttractionLocaleStatusEnum.REVIEWED) mark = 'reviewed';
        else if (hasLocaleContent(locale?.content) || locale?.title) mark = 'filled';

        return { value: l, label: l.toUpperCase(), mark };
    });

    const tabs = isCreate
        ? [{ value: 'details', label: t('editors.attraction.tabs.details'), dirty: tabDirty }]
        : [
              {
                  value: 'details',
                  label: t('editors.attraction.tabs.details'),
                  dirty: tab === 'details' && tabDirty,
              },
              {
                  value: 'locales',
                  label: t('editors.attraction.tabs.locales'),
                  dirty: tab === 'locales' && tabDirty,
              },
              {
                  value: 'media',
                  label: t('editors.attraction.tabs.media'),
                  dirty: tab === 'media' && tabDirty,
              },
              {
                  value: 'artifacts',
                  label: t('editors.attraction.tabs.artifacts'),
                  dirty: tab === 'artifacts' && tabDirty,
              },
              { value: 'facts', label: t('editors.attraction.tabs.facts') },
              { value: 'brief', label: t('editors.attraction.tabs.brief') },
              { value: 'review', label: t('editors.attraction.tabs.review') },
          ];

    return (
        <EntityEditorShell
            zIndex={zIndex}
            opened={opened}
            onClose={onClose}
            loading={loading}
            title={
                isCreate
                    ? t('editors.attraction.newTitle')
                    : entity?.displayTitle ||
                      entity?.title ||
                      t('editors.attraction.fallbackTitle', { id: attractionId })
            }
            subtitle={entity?.cityTitle || undefined}
            status={
                entity?.reviewStatus
                    ? {
                          label: enumLabel(t, 'enums.attractionReviewStatus', entity.reviewStatus),
                          color: reviewColor[entity.reviewStatus] || 'gray',
                      }
                    : undefined
            }
            dirty={tabDirty}
            tabs={tabs}
            activeTab={tab}
            onTabChange={handleTabChange}
            size="xl"
            headerRight={
                entity?.readiness ? (
                    <AttractionReadinessBadge readiness={entity.readiness} withNext />
                ) : undefined
            }
        >
            {tab === 'details' && (
                <Stack gap="lg" pb={40}>
                    {!isCreate && attractionId != null ? (
                        <StaffNotesPanel attractionId={attractionId} />
                    ) : null}
                    <Section title={t('editors.attraction.identity.title')}>
                        <TextInput
                            label={t('editors.attraction.identity.titleLabel')}
                            required
                            {...form.getInputProps('title')}
                        />
                        <TextInput
                            label={t('editors.attraction.identity.address')}
                            {...form.getInputProps('address')}
                        />
                        <CitySearchSelect
                            value={form.values.cityId}
                            onChange={(cityId) => {
                                form.setFieldValue('cityId', cityId);
                                if (cityId !== form.values.cityId) {
                                    form.setFieldValue('districtId', null);
                                }
                            }}
                            initialLabel={entity?.cityTitle}
                        />
                        <DistrictSelect
                            cityId={form.values.cityId}
                            value={form.values.districtId}
                            onChange={(districtId) => form.setFieldValue('districtId', districtId)}
                        />
                        {isCreate && (
                            <Select
                                label={t('editors.attraction.identity.country')}
                                data={countryOptions}
                                searchable
                                required
                                {...form.getInputProps('country')}
                            />
                        )}
                        <Select
                            label={t('editors.attraction.identity.category')}
                            data={categoryOptions}
                            searchable
                            {...form.getInputProps('category')}
                        />
                        <Select
                            label={t('editors.attraction.identity.fame')}
                            data={fameOptions}
                            searchable
                            {...form.getInputProps('fame')}
                        />
                        <Group grow>
                            <NumberInput
                                label={t('editors.attraction.identity.lat')}
                                decimalScale={6}
                                {...form.getInputProps('lat')}
                            />
                            <NumberInput
                                label={t('editors.attraction.identity.lng')}
                                decimalScale={6}
                                {...form.getInputProps('lng')}
                            />
                        </Group>
                        {isCreate && (
                            <>
                                <TextInput
                                    label={t('editors.attraction.identity.knownForEn')}
                                    description={t('editors.attraction.identity.knownForHint')}
                                    {...form.getInputProps('knownFor')}
                                />
                                <Textarea
                                    label={t('editors.attraction.identity.descriptionEn')}
                                    description={t('editors.attraction.identity.descriptionHint')}
                                    minRows={4}
                                    autosize
                                    {...form.getInputProps('description')}
                                />
                            </>
                        )}
                    </Section>
                    {!isCreate && (
                        <Section title={t('editors.attraction.attributes.title')}>
                            <MultiSelect
                                label={t('editors.attraction.attributes.tags')}
                                description={t('editors.attraction.attributes.tagsHint')}
                                data={enumOptions(
                                    Object.values(AttractionThemeEnum),
                                    t,
                                    'enums.attractionTheme',
                                )}
                                searchable
                                {...form.getInputProps('tags')}
                            />
                            <Select
                                label={t('editors.attraction.attributes.audioPotential')}
                                description={
                                    entity?.audioPotential
                                        ? t('editors.attraction.attributes.audioHint')
                                        : entity?.audioPotentialEffective
                                          ? t('editors.attraction.attributes.audioFromScout', {
                                                value: enumLabel(
                                                    t,
                                                    'enums.attractionAudioPotential',
                                                    entity.audioPotentialEffective,
                                                ),
                                            })
                                          : t('editors.attraction.attributes.audioHint')
                                }
                                data={enumOptions(
                                    Object.values(AttractionAudioPotentialEnum),
                                    t,
                                    'enums.attractionAudioPotential',
                                )}
                                clearable
                                {...form.getInputProps('audioPotential')}
                            />
                            <Group grow>
                                <NumberInput
                                    label={t('editors.attraction.attributes.mustSeeScore')}
                                    min={1}
                                    max={10}
                                    {...form.getInputProps('mustSeeScore')}
                                />
                                <NumberInput
                                    label={t('editors.attraction.attributes.wowScore')}
                                    min={1}
                                    max={10}
                                    {...form.getInputProps('wowScore')}
                                />
                            </Group>
                            <Group grow>
                                <NumberInput
                                    label={t('editors.attraction.attributes.touristCrowdScore')}
                                    min={1}
                                    max={10}
                                    {...form.getInputProps('touristCrowdScore')}
                                />
                                <NumberInput
                                    label={t('editors.attraction.attributes.localExperienceScore')}
                                    min={1}
                                    max={10}
                                    {...form.getInputProps('localExperienceScore')}
                                />
                            </Group>
                            <Group grow>
                                <Select
                                    label={t('editors.attraction.attributes.routeRole')}
                                    data={routeRoleOptions}
                                    clearable
                                    {...form.getInputProps('routeRole')}
                                />
                                <Select
                                    label={t('editors.attraction.attributes.indoorOutdoor')}
                                    data={indoorOutdoorOptions}
                                    clearable
                                    {...form.getInputProps('indoorOutdoor')}
                                />
                            </Group>
                            <Group grow align="flex-end">
                                <Select
                                    label={t('editors.attraction.layers.tier')}
                                    description={t('editors.attraction.layers.tierHint')}
                                    data={tierOptions}
                                    clearable
                                    {...form.getInputProps('tier')}
                                />
                                {form.values.tier === AttractionTierEnum.LIGHT && (
                                    <Switch
                                        label={t('editors.attraction.layers.lightApproved')}
                                        {...form.getInputProps('lightApproved', {
                                            type: 'checkbox',
                                        })}
                                    />
                                )}
                            </Group>
                            {(entity?.triage || entity?.scout) && (
                                <Text size="sm" c="dimmed">
                                    {[
                                        entity?.triage &&
                                            t('editors.attraction.layers.triage', {
                                                verdict: entity.triage.verdict,
                                                interest: entity.triage.interest ?? '—',
                                            }),
                                        entity?.scout &&
                                            t('editors.attraction.layers.scout', {
                                                score: entity.scout.score,
                                                verdict: enumLabel(
                                                    t,
                                                    'enums.attractionScoutVerdict',
                                                    entity.scout.verdict,
                                                ),
                                                hooks: entity.scout.hooks.join(' · '),
                                            }),
                                    ]
                                        .filter(Boolean)
                                        .join(' — ')}
                                </Text>
                            )}
                            <TextInput
                                label={t('editors.attraction.attributes.wikidataId')}
                                {...form.getInputProps('wikidataId')}
                            />
                            <Switch
                                label={t('editors.attraction.attributes.isFreePreview')}
                                {...form.getInputProps('isFreePreview', { type: 'checkbox' })}
                            />
                        </Section>
                    )}
                    <Section
                        title={t('editors.attraction.visit.title')}
                        description={t('editors.attraction.visit.hint')}
                    >
                        <WeekHoursFields
                            hours={form.values.hours}
                            onChange={(hours) => form.setFieldValue('hours', hours)}
                        />
                        <Group grow>
                            <NumberInput
                                label={t('editors.attraction.visit.durationMin')}
                                min={0}
                                {...form.getInputProps('visitDurationMin')}
                            />
                            <NumberInput
                                label={t('editors.attraction.visit.durationMax')}
                                min={0}
                                {...form.getInputProps('visitDurationMax')}
                            />
                        </Group>
                        <Switch
                            label={t('editors.attraction.visit.isFree')}
                            {...form.getInputProps('isFree', { type: 'checkbox' })}
                        />
                        <Switch
                            label={t('editors.attraction.visit.ticketRequired')}
                            {...form.getInputProps('ticketRequired', { type: 'checkbox' })}
                        />
                        <Switch
                            label={t('editors.attraction.visit.bookingRequired')}
                            {...form.getInputProps('bookingRequired', { type: 'checkbox' })}
                        />
                        <Switch
                            label={t('editors.attraction.visit.familyFriendly')}
                            {...form.getInputProps('familyFriendly', { type: 'checkbox' })}
                        />
                        <Switch
                            label={t('editors.attraction.visit.rainFriendly')}
                            {...form.getInputProps('rainFriendly', { type: 'checkbox' })}
                        />
                        <Switch
                            label={t('editors.attraction.visit.nightFriendly')}
                            {...form.getInputProps('nightFriendly', { type: 'checkbox' })}
                        />
                    </Section>
                    {!isCreate && (
                        <Section title={t('editors.attraction.actions.title')}>
                            <Group>
                                <Button
                                    variant="light"
                                    loading={action === 'deepen'}
                                    disabled={action != null && action !== 'deepen'}
                                    onClick={deepenResearch}
                                >
                                    {t('editors.attraction.actions.deepen')}
                                </Button>
                                <Button
                                    variant="light"
                                    loading={action === 'cover'}
                                    disabled={action != null && action !== 'cover'}
                                    onClick={generateCoverImage}
                                >
                                    {t('editors.attraction.actions.generateCover')}
                                </Button>
                                <Button
                                    variant="light"
                                    color={entity?.archived ? 'gray' : 'orange'}
                                    loading={action === 'archive'}
                                    disabled={action != null && action !== 'archive'}
                                    onClick={toggleArchive}
                                >
                                    {entity?.archived
                                        ? t('editors.attraction.actions.unarchive')
                                        : t('editors.attraction.actions.archive')}
                                </Button>
                            </Group>
                        </Section>
                    )}
                    <EditorFooter
                        onCancel={onClose}
                        onSave={saveDetails}
                        saving={saving}
                        saveLabel={isCreate ? t('common.create') : t('common.save')}
                        left={
                            !isCreate ? (
                                <Button
                                    color="red"
                                    variant="light"
                                    loading={deleting}
                                    onClick={remove}
                                >
                                    {t('common.delete')}
                                </Button>
                            ) : undefined
                        }
                    />
                </Stack>
            )}

            {tab === 'locales' && !isCreate && (
                <Stack gap="lg" pb={40}>
                    <Group justify="space-between" align="center">
                        <LanguageChips
                            languages={chips}
                            value={lang}
                            onChange={(v) => setLang(v as Languages)}
                        />
                        <Button
                            size="xs"
                            variant="light"
                            loading={action === 'regenerate'}
                            disabled={action != null && action !== 'regenerate'}
                            onClick={regenerateContent}
                        >
                            {t('editors.attraction.actions.regenerate')}
                        </Button>
                    </Group>
                    <Section title={t('editors.attraction.copy.title')}>
                        <TextInput
                            label={t('editors.attraction.copy.localeTitle')}
                            value={localeDraft.title}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, title: e.currentTarget.value }))
                            }
                        />
                        <TextInput
                            label={t('editors.attraction.copy.knownFor')}
                            value={localeDraft.knownFor}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, knownFor: e.currentTarget.value }))
                            }
                        />
                        <Textarea
                            label={t('editors.attraction.visit.hoursNote')}
                            description={t('editors.attraction.visit.hoursNoteHint')}
                            minRows={2}
                            autosize
                            value={localeDraft.hoursNote}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, hoursNote: e.currentTarget.value }))
                            }
                        />
                        <Textarea
                            label={t('editors.attraction.visit.priceNote')}
                            description={t('editors.attraction.visit.priceNoteHint')}
                            minRows={2}
                            autosize
                            maxLength={400}
                            value={localeDraft.priceNote}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, priceNote: e.currentTarget.value }))
                            }
                        />
                        <Textarea
                            label={t('editors.attraction.visit.visitNote')}
                            description={t('editors.attraction.visit.visitNoteHint')}
                            minRows={2}
                            autosize
                            maxLength={800}
                            value={localeDraft.visitNote}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, visitNote: e.currentTarget.value }))
                            }
                        />
                        <Textarea
                            label={t('editors.attraction.copy.description')}
                            minRows={3}
                            value={localeDraft.description}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    description: e.currentTarget.value,
                                }))
                            }
                        />
                    </Section>
                    <Section title={t('editors.attraction.guidePackage.title')}>
                        <Textarea
                            label={t('editors.attraction.guidePackage.hook')}
                            minRows={2}
                            value={localeDraft.content.hook}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    content: { ...d.content, hook: e.currentTarget.value },
                                }))
                            }
                        />
                        <Textarea
                            label={t('editors.attraction.guidePackage.guideTalk')}
                            minRows={4}
                            value={localeDraft.content.guideTalk}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    content: { ...d.content, guideTalk: e.currentTarget.value },
                                }))
                            }
                        />
                        {(localeDraft.content.cards || []).map((card, index) => (
                            <Stack
                                key={`card-${index}`}
                                gap="xs"
                                p="md"
                                style={{
                                    border: '1px solid var(--mantine-color-gray-3)',
                                    borderRadius: 'var(--mantine-radius-md)',
                                }}
                            >
                                <Group justify="space-between">
                                    <Text size="sm" fw={600}>
                                        {t('editors.attraction.guidePackage.cardN', {
                                            n: index + 1,
                                        })}
                                    </Text>
                                    <Button
                                        size="xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() =>
                                            setLocaleDraft((d) => ({
                                                ...d,
                                                content: {
                                                    ...d.content,
                                                    cards: (d.content.cards || []).filter(
                                                        (_, i) => i !== index,
                                                    ),
                                                },
                                            }))
                                        }
                                    >
                                        {t('editors.attraction.guidePackage.removeCard')}
                                    </Button>
                                </Group>
                                <Group align="flex-start" wrap="nowrap">
                                    <TextInput
                                        label={t('editors.attraction.guidePackage.cardEmoji')}
                                        w={70}
                                        value={card.emoji}
                                        onChange={(e) =>
                                            patchCard(index, { emoji: e.currentTarget.value })
                                        }
                                    />
                                    <TextInput
                                        label={t('editors.attraction.guidePackage.cardTitle')}
                                        style={{ flex: 1 }}
                                        value={card.title}
                                        onChange={(e) =>
                                            patchCard(index, { title: e.currentTarget.value })
                                        }
                                    />
                                </Group>
                                <Textarea
                                    label={t('editors.attraction.guidePackage.cardBody')}
                                    minRows={2}
                                    value={card.body}
                                    onChange={(e) =>
                                        patchCard(index, { body: e.currentTarget.value })
                                    }
                                />
                            </Stack>
                        ))}
                        <Button
                            variant="light"
                            leftSection={<IconPlus size={16} />}
                            onClick={() =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    content: {
                                        ...d.content,
                                        cards: [
                                            ...(d.content.cards || []),
                                            { emoji: '', title: '', body: '' },
                                        ],
                                    },
                                }))
                            }
                        >
                            {t('editors.attraction.guidePackage.addCard')}
                        </Button>
                        <Group align="flex-start" wrap="nowrap">
                            <TextInput
                                label={t('editors.attraction.guidePackage.funFactEmoji')}
                                w={70}
                                value={localeDraft.content.funFact?.emoji || ''}
                                onChange={(e) => patchFunFact({ emoji: e.currentTarget.value })}
                            />
                            <TextInput
                                label={t('editors.attraction.guidePackage.funFactTitle')}
                                style={{ flex: 1 }}
                                value={localeDraft.content.funFact?.title || ''}
                                onChange={(e) => patchFunFact({ title: e.currentTarget.value })}
                            />
                            <Select
                                label={t('editors.attraction.guidePackage.funFactKind')}
                                data={funFactKindOptions}
                                value={
                                    localeDraft.content.funFact?.kind ||
                                    AttractionFunFactKindEnum.FACT
                                }
                                onChange={(v) =>
                                    patchFunFact({
                                        kind:
                                            (v as AttractionFunFactKindEnum) ||
                                            AttractionFunFactKindEnum.FACT,
                                    })
                                }
                            />
                        </Group>
                        <Textarea
                            label={t('editors.attraction.guidePackage.funFact')}
                            minRows={2}
                            value={localeDraft.content.funFact?.body || ''}
                            onChange={(e) => patchFunFact({ body: e.currentTarget.value })}
                        />
                        <TextInput
                            label={t('editors.attraction.guidePackage.lookFor')}
                            value={(localeDraft.content.lookFor || []).join(', ')}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    content: {
                                        ...d.content,
                                        lookFor: e.currentTarget.value
                                            .split(',')
                                            .map((s) => s.trim())
                                            .filter(Boolean),
                                    },
                                }))
                            }
                        />
                        <MediaDropzone
                            label={t('editors.attraction.guidePackage.guideAudio')}
                            kind="audio"
                            value={localeDraft.guideAudioUrl}
                            onChange={(url) =>
                                setLocaleDraft((d) => ({ ...d, guideAudioUrl: url }))
                            }
                        />
                        <Group>
                            <Button
                                size="xs"
                                variant="light"
                                loading={action === 'tts'}
                                disabled={
                                    (action != null && action !== 'tts') ||
                                    !localeDraft.content.guideTalk?.trim()
                                }
                                onClick={generateTts}
                            >
                                {t('editors.attraction.guidePackage.generateTts')}
                            </Button>
                        </Group>
                        <Select
                            label={t('editors.attraction.guidePackage.localeStatus')}
                            data={localeStatusOptions}
                            value={localeDraft.status}
                            onChange={(v) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    status: (v as AttractionLocaleStatusEnum) || d.status,
                                }))
                            }
                        />
                    </Section>
                    <EditorFooter
                        onCancel={onClose}
                        onSave={() => saveLocale()}
                        saving={saving}
                        saveLabel={t('editors.attraction.saveLocale')}
                        left={
                            <Button
                                variant="light"
                                color="green"
                                loading={saving}
                                onClick={() => saveLocale(AttractionLocaleStatusEnum.REVIEWED)}
                            >
                                {t('editors.attraction.saveMarkReviewed')}
                            </Button>
                        }
                    />
                </Stack>
            )}

            {tab === 'media' && !isCreate && (
                <Stack gap="lg" pb={40}>
                    <Section
                        title={t('editors.attraction.photos.title')}
                        description={t('editors.attraction.photos.description')}
                    >
                        <PhotoUpload
                            variant="gallery"
                            label={t('editors.attraction.photos.gallery')}
                            value={photos}
                            onChange={setPhotos}
                            imageType={ImageTypeEnum.CITY_ATTRACTION}
                        />
                    </Section>
                    <EditorFooter
                        onCancel={onClose}
                        onSave={savePhotos}
                        saving={saving}
                        saveLabel={t('editors.attraction.savePhotos')}
                        disabled={!photosDirty}
                    />
                </Stack>
            )}

            {tab === 'artifacts' && !isCreate && (
                <Stack gap="lg" pb={40}>
                    <ArtifactsPanel artifacts={artifacts} onChange={setArtifacts} />
                    <EditorFooter
                        onCancel={onClose}
                        onSave={saveArtifacts}
                        saving={saving}
                        saveLabel={t('editors.attraction.saveArtifacts')}
                        disabled={!artifactsDirty}
                    />
                </Stack>
            )}

            {tab === 'facts' && attractionId != null && (
                <FactsPanel attractionId={attractionId} onChanged={syncReadiness} />
            )}

            {tab === 'brief' && attractionId != null && (
                <StoryBriefPanel attractionId={attractionId} onChanged={syncReadiness} />
            )}

            {tab === 'review' && !isCreate && (
                <Stack gap="lg">
                    <Section title={t('editors.attraction.moderation.title')}>
                        <Textarea
                            label={t('editors.attraction.moderation.rejectionReason')}
                            minRows={3}
                            {...form.getInputProps('rejectionReason')}
                        />
                        <Group justify="flex-end">
                            <Button
                                color="red"
                                variant="light"
                                loading={moderating}
                                onClick={() => moderate(AttractionReviewStatusEnum.REJECTED)}
                            >
                                {t('editors.attraction.moderation.reject')}
                            </Button>
                            <Button
                                color="green"
                                loading={moderating}
                                onClick={() => moderate(AttractionReviewStatusEnum.APPROVED)}
                            >
                                {t('editors.attraction.moderation.approve')}
                            </Button>
                        </Group>
                    </Section>
                    <EditorFooter onCancel={onClose} />
                </Stack>
            )}
        </EntityEditorShell>
    );
};
